import React, { useMemo } from "react";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Button,
  Row,
  Col,
  Modal
} from "react-bootstrap";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { useDispatch } from "react-redux";
import { addNewModule, editModule, getModulesList } from "store/services/emailPreferenceService";
import { ROLE_TYPES, STATUS_ENUM } from "common/constants";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { formatText } from "helpers/common";

const AddNewPreferencePopup = ({ showPopup, closePopup, data }) => {
  console.log("data", data)
  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    return {
      type: "preference",
      module_id: data?.module_id,
      user_type: data?.user_type || 3,
      name: data?.name || "",
      slug: data?.slug || "",
      description: data?.description || "",
      status: data?.status || "active"
    }
  }, [data])

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Preference name is required"),
      slug: Yup.string()
        .matches(/^[a-z0-9_]+$/, "Only lowercase letters, numbers and underscores allowed")
        .required("Slug is required"),
      description: Yup.string().required("Description is required"),
      status: Yup.string().required("Description is required"),
      user_type: Yup.number().required("User type is required"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if(!data?.id) {
          await dispatch(addNewModule(values));
        } else {
          const payload = {
            id: data?.id,
            values,
          }

          await dispatch(editModule(payload));
        }
        await dispatch(getModulesList());
        resetForm();
        closePopup();
      } catch (err) {
        console.error("Error adding new preference", err);
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    closePopup();
  }

  return (
    <Modal
      show={showPopup}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>{data?.id ? "Edit" : "Add"} Preference</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Row className="align-items-end">
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <label>Preference Name</label>
                  <AppInputField
                    name="name"
                    placeholder="Enter preference name"
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <label>Slug</label>
                  <AppInputField
                    name="slug"
                    placeholder="Enter preference slug"
                    disabled={!!data?.id}
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <Form.Label>Description</Form.Label>
                  <AppInputField
                    name="description"
                    fieldType="textarea"
                    placeholder="Short description of this module"
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <label>Panel Name</label>
                  <AppSelectField
                    name="user_type"
                    options={Object.entries(ROLE_TYPES).map(([label, value]) => {
                      return {
                        label: formatText(label),
                        value
                      }
                    } )}
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <Form.Label>Status</Form.Label>
                  <div className="d-flex gap-10 align-items-center">
                    {STATUS_ENUM.map((option, index) => (
                      <div className="form-group d-flex align-items-center" key={index}>
                        <input
                          type="radio"
                          className="form-radio"
                          name="status"
                          onChange={(e) => formik.setFieldValue("status", e.target.checked)}
                          value={formik.values.status}
                          checked={formik.values.status === option.value}
                        />
                        <label className="m-0">&nbsp;{option.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col md={12} className="d-flex justify-content-end gap-10">
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-3"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={formik.isSubmitting}
                  className="mt-3"
                >
                  {formik.isSubmitting ? "Please wait..." : data?.id ? "Update Preference" : "Add Preference"}
                </Button>
              </Col>
            </Row>
          </Form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
};

export default AddNewPreferencePopup;
